"use client";

import {
    createContext,
    useContext,
    useState,
    useCallback,
    useMemo,
} from "react";
import type { Editor } from "@tiptap/react";

interface ToolbarContextValue {
    /** The editor that currently owns the toolbar (last focused editable editor). */
    editor: Editor | null;
    /** Register an editor as the active toolbar editor (called on focus). */
    activate: (editor: Editor) => void;
    /** Deactivate only if the given editor is the current one (called on unmount / click-outside). */
    deactivate: (editor: Editor) => void;
    toolbarRoot: HTMLElement | null;
    setToolbarRoot: (el: HTMLElement | null) => void;
}

const RichTextToolbarContext = createContext<ToolbarContextValue | null>(null);

export function RichTextToolbarProvider({
    children,
}: {
    children: React.ReactNode;
}) {
    const [editor, setEditor] = useState<Editor | null>(null);
    const [toolbarRoot, setToolbarRoot] = useState<HTMLElement | null>(null);

    const activate = useCallback((ed: Editor) => {
        setEditor(ed);
    }, []);

    const deactivate = useCallback((ed: Editor) => {
        setEditor((prev) => (prev === ed ? null : prev));
    }, []);

    const value = useMemo(
        () => ({
            editor,
            activate,
            deactivate,
            toolbarRoot,
            setToolbarRoot,
        }),
        [editor, activate, deactivate, toolbarRoot]
    );

    return (
        <RichTextToolbarContext.Provider value={value}>
            {children}
        </RichTextToolbarContext.Provider>
    );
}

export function useRichTextToolbar() {
    const ctx = useContext(RichTextToolbarContext);
    if (!ctx) {
        throw new Error(
            "useRichTextToolbar must be used within RichTextToolbarProvider"
        );
    }
    return ctx;
}
